import { useIsLoggedInRetailerOrBrandPreview } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { getIsRobot } from "@faire/retailer-visitor-shared/serialized-data/getIsRobot";
import { getSettingEnableSEOLanguageSelector } from "@faire/retailer-visitor-shared/settings/getSettingEnableSEOLanguageSelector";
import { getSettingLanguageSelectorEnabled } from "@faire/retailer-visitor-shared/settings/getSettingLanguageSelectorEnabled";
import { useSettingMigrateRetailerStore } from "@faire/retailer-visitor-shared/settings/useSettingMigrateRetailerStore";
import { useRetailerStore } from "@faire/retailer-visitor-shared/stores/domain/RetailerStore";
import { getGlobalProperty } from "@faire/web--source/common/globals/getGlobalProperty";
import { getCurrentPath } from "@faire/web--source/common/Path";
import { pathMatchesRoute } from "@faire/web--source/common/routes/RouteMatcher";
import { ILocaleKey } from "@faire/web-api--source/indigofair/locale/ILocaleKey";
import { route as faireDirectRoute } from "@faire/web-api--source/routes/www/direct/brandAlias";
import { route as faireDirectSalesRepRoute } from "@faire/web-api--source/routes/www/direct/brandAlias/rep/salesRepCode";
import { route as discoverRoute } from "@faire/web-api--source/routes/www/discover/searchTerm";
import { route as exploreRoute } from "@faire/web-api--source/routes/www/explore/searchTerm";
import { route as searchRoute } from "@faire/web-api--source/routes/www/search";
import { useMemo } from "react";

const useLanguageControl = () => {
  const {
    showLanguageSelector,
    showSEOLanguageSelector,
    availableLanguageSelectorLocales,
  } = useRetailerStore([
    "showLanguageSelector",
    "showSEOLanguageSelector",
    "availableLanguageSelectorLocales",
  ]);

  return {
    showLanguageSelector,
    showSEOLanguageSelector,
    availableLanguageSelectorLocales,
  };
};

const useLanguageTreatment = () => {
  const isLoggedInRetailerOrBrandPreview =
    useIsLoggedInRetailerOrBrandPreview();
  const showLanguageSelector =
    !getIsRobot() && getSettingLanguageSelectorEnabled();
  const showSEOLanguageSelector = useMemo(() => {
    /**
     * we want to hide language selector on discover/search and explore page
     * as search terms do not translate well
     * This would also preserve crawl budget
     */
    const blocklist = [
      discoverRoute,
      searchRoute,
      exploreRoute,
      faireDirectRoute,
      faireDirectSalesRepRoute,
    ];
    const hideLanguageSelector = blocklist.some((route) =>
      pathMatchesRoute(getCurrentPath().pathname, route)
    );
    return (
      !!getIsRobot() &&
      !hideLanguageSelector &&
      !isLoggedInRetailerOrBrandPreview &&
      getSettingEnableSEOLanguageSelector()
    );
  }, [isLoggedInRetailerOrBrandPreview]);

  const availableLanguageSelectorLocales = useMemo(
    () =>
      getGlobalProperty<ILocaleKey[]>("availableLanguageSelectorLocales") ?? [],
    []
  );

  return {
    showLanguageSelector,
    showSEOLanguageSelector,
    availableLanguageSelectorLocales,
  };
};

export const useLanguage = () => {
  const shouldUseHooksMigration = useSettingMigrateRetailerStore();
  if (shouldUseHooksMigration) {
    // eslint-disable-next-line react-compiler/react-compiler, react-hooks/rules-of-hooks
    return useLanguageTreatment();
  }
  // eslint-disable-next-line react-compiler/react-compiler, react-hooks/rules-of-hooks
  return useLanguageControl();
};
